import { useEffect, useRef, useState } from 'react';
import { IconClose } from './Icons';

function mmss(totalSeconds: number): string {
  const sign = totalSeconds < 0 ? '+' : '';
  const s = Math.abs(totalSeconds);
  return `${sign}${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Counts down from the rest target, then keeps counting up so you can see how
 * long you actually took. Driven off a wall-clock timestamp rather than an
 * accumulating counter, so it stays right when the phone screen sleeps mid-set.
 */
export function RestTimer({
  startedAt,
  seconds,
  onDismiss,
}: {
  startedAt: number;
  seconds: number;
  onDismiss: () => void;
}) {
  const [remaining, setRemaining] = useState(() =>
    Math.round(seconds - (Date.now() - startedAt) / 1000),
  );
  const buzzed = useRef(false);

  useEffect(() => {
    const tick = () => {
      const left = Math.round(seconds - (Date.now() - startedAt) / 1000);
      setRemaining(left);
      if (left <= 0 && !buzzed.current) {
        buzzed.current = true;
        if ('vibrate' in navigator) navigator.vibrate?.([120, 60, 120]);
      }
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [startedAt, seconds]);

  const over = remaining <= 0;

  return (
    <div className={`resttimer${over ? ' over' : ''}`} role="timer" aria-live="off">
      <span className="tiny faint" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {over ? 'Go' : 'Rest'}
      </span>
      <span className="t">{mmss(remaining)}</span>
      <button className="btn ghost sm" onClick={onDismiss} aria-label="Dismiss rest timer">
        <IconClose size={15} />
      </button>
    </div>
  );
}
